import { usePageLayout, useUserSession } from '@libs/common-next';

/**
 * Retrieves the customer key from multiple sources and returns it as a string.
 *
 * This function extracts the customer key by attempting to fetch it from three different sources:
 * - From the OCAPI session data.
 * - From the user data associated with the page layout.
 * - From the user data stored in the session.
 *
 * The priority of retrieval is as follows:
 * 1. OCAPI session data.
 * 2. Page layout user data.
 * 3. User session data.
 *
 * If a customer key is found in any of the sources, it is returned as a string.
 * If no customer key is found in any source, the value returned is undefined.
 *
 * @function
 * @returns { customerKey: string | undefined } An object containing the `customerKey` property, which is either a string representation of the customer key or undefined.
 */
export const useCustomerKey = (): { customerKey: string | undefined } => {
    const { ocapi, userData: userDataPageLayout } = usePageLayout();
    const { userData: userDataSession } = useUserSession();

    const customerKeyOcapi = ocapi?.data?.session.customer?.c_petmToken;
    const customerKeyUserPageLayout = userDataPageLayout?.profile?.customerKey;
    const customerKeyUserSession = userDataSession?.profile?.customerKey;
    const customerKey = customerKeyOcapi || customerKeyUserPageLayout || customerKeyUserSession; // NOSONAR

    return { customerKey: customerKey ? String(customerKey) : undefined };
};
